/**
 * The sign-in code screen's words, from the server's second-step answer
 * (`/api/auth/login`, `GET /api/auth/pending-2fa`, `POST /api/auth/resend-2fa`).
 * Plan: docs/plans/2026-10-07-well-2fa-enforced-and-sms.md
 */

export interface SecondStep {
  method: 'email' | 'sms' | 'totp';
  hint: string;
  /** Where the code went ("We texted a code to (•••) •••-0123."). Absent for
   *  an authenticator, and while a code is still owed after Google sign-in. */
  notice?: string;
}

/** The line above the code box. The server's notice when there is one — it is
 *  the only place that knows a text fell back to email. */
export function secondStepPrompt(step: SecondStep, sending: boolean): string {
  if (step.method === 'totp') return 'Enter the code from your authenticator app, or a backup code';
  if (sending) return 'Sending your code…';
  if (step.notice) return step.notice;
  return step.method === 'sms' ? `Enter the code sent to ***${step.hint}` : `Enter the code sent to ${step.hint}`;
}

/** The authenticator's backup codes: 10 letters and digits (`generateBackupCodes`). */
const BACKUP_CODE_LENGTH = 10;

/**
 * What the code box keeps of what was typed. An authenticator user may type a
 * backup code instead (10 letters and digits); everyone else a 6-digit code.
 * Until 2026-10-08 the box kept 6 digits only, so a backup code could never be
 * entered at sign-in — the manual test of this plan found it.
 */
export function cleanCodeInput(method: SecondStep['method'], raw: string): string {
  if (method !== 'totp') return raw.replace(/\D/g, '').slice(0, 6);
  return raw.replace(/[^0-9a-z]/gi, '').toUpperCase().slice(0, BACKUP_CODE_LENGTH);
}

/** Whether the box holds something worth sending: 6 digits, or a backup code. */
export function codeComplete(method: SecondStep['method'], code: string): boolean {
  return /^\d{6}$/.test(code) || (method === 'totp' && code.length === BACKUP_CODE_LENGTH);
}

